import { DashboardService } from "@app/main/modules/dashboard/dashboard.service";
import { FleetNavigationModule } from "@app/main/modules/fleet/fleet.navigation";
import * as angular from "angular";
import * as moment from "moment";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "../../../../directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "../../../../directives/_veri/veriTable/veriTable.service";
import { SortType } from "../../../../enum/SortType";
import { IDriver } from "../../../../interfaces/IDriver";
import { IPlot } from "../../../../interfaces/IPlot";
import { NavigationService } from "../../../../services/navigation.service";
import { PlotService } from "../../../../services/plot.service";
import { IDriverTachoHours, TachoService } from "../../../../services/tacho.service";
import { DynamicReportService } from "../../reports/_services/dynamicReports.service";
import { ReportTableRowDataSource } from "../../reports/enums/ReportTableRowDataSource";
import { IColumnHeader } from "../../reports/models/IColumnHeader";
import { IDataDefinition } from "../../reports/models/IReportDefinition";
import "./driversPage.component.scss";

class Controller implements angular.IComponentController {
    private driverTachoHours: IDriverTachoHours[];
    private latestPlots: IPlot[];
    private report: IDataDefinition;

    loading = false;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IDriver>[] = [];

    static $inject = ["plotService", "veriTableService", "tachoService", "dashboardService", "navigationService"];

    constructor(
        private readonly plotService: PlotService,
        private readonly veriTableService: VeriTableService,
        private readonly tachoService: TachoService,
        private readonly dashboardService: DashboardService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly navigationService: NavigationService) {
    }

    $onInit(): void {
        this.createTableHeaders();
        this.getData();
    }

    selectDriver(driver: IDriverTachoHours) {
        this.navigationService.go(FleetNavigationModule.DriverView, { driverId: driver.DriverId });
    }

    // ---

    private getData() {
        this.loading = true;

        const reportPromise = this.createReport();
        const latestPlotsPromise = this.plotService.getLatestPlots() as Promise<IPlot[]>;
        const tachoPromise = this.tachoService.getCurrentTachoForAllDrivers(true) as Promise<IDriverTachoHours[]>;

        Promise.all([reportPromise, tachoPromise, latestPlotsPromise])
            .then(([report, tacho, latestPlots]) => {
                this.driverTachoHours = tacho;
                this.latestPlots = latestPlots;
                this.report = report;

                this.createTableRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createTableHeaders() {
        this.tableHeaders = [
            this.veriTableService.createVehicleRegHeader(),
            { title: "Name", sortable: true, },
            { title: "Location", sortable: true, },
            this.veriTableService.createDateTimeHeader("Last Update"),
            { title: "Tacho State", sortable: true, width: 140 },
            { title: "Remaining Drive", sortable: true, sortType: SortType.Number, width: 140 },
            { title: "Driver Score", sortable: true, sortType: SortType.Number, width: 120 },
        ];
    }

    private createTableRows(): void {
        this.tableRows = this.veriTableService.createTableRowsWithCells(this.driverTachoHours, data => this.createTableRowCells(data));
    }

    private createTableRowCells(tachoHours: IDriverTachoHours): IVeriTableRowCell[] {
        var matchingReportRow = this.report.TableDefinition.Rows.find(row => row.Parameters.DriverId == tachoHours.DriverId);
        var latestPlot: IPlot = null;

        if (tachoHours.Driver.vehicle) {
            latestPlot = this.latestPlots.find(p => p.VehicleId == tachoHours.Driver.vehicle.Id);
        }

        return [
            this.veriTableService.createVehicleRegCell(tachoHours.Driver.vehicle),
            { contents: tachoHours.Driver.Name },
            latestPlot
                ? this.veriTableService.createLocationInformationCell(latestPlot.Id, "row.data", latestPlot)
                : this.veriTableService.createNACell()
            ,
            this.veriTableService.createDateTimeCell(latestPlot?.Utc),
            { contents: `<tacho-mode class="mr-5" [tacho-mode]="${tachoHours.Mode}"></tacho-mode><tacho-mode-status [tacho-mode]="${tachoHours.Mode}"></tacho-mode-status>` },
            { contents: `{{ ${tachoHours.RemainingDrive} | minutesToDuration : 60000: 'N/A' }}`, data: tachoHours.RemainingDrive, sortData: tachoHours.RemainingDrive },
            this.veriTableService.createScoreCell(matchingReportRow?.Cells[1])
        ];
    }

    private createReport(): Promise<IDataDefinition> {
        var dataDefinition: IDataDefinition = {
            TableDefinition: { Headers: [] },
            RowDataSource: ReportTableRowDataSource.Driver,
            Parameters: {
                Start: moment().startOf("day").add(-6, "d").toDate(),
                End: moment().endOf("day").toDate()
            }
        };

        return (this.dynamicReportService.getColumnHeaders() as Promise<IColumnHeader[]>).then(headers => {
            var drivingFuelColumn = this.dashboardService.findColumn("DrivingFuelEfficiency", headers, "Journey");
            var totalScoreColumn = this.dashboardService.findColumn("Total Score", headers, "Journey");

            dataDefinition.TableDefinition.Headers.push(totalScoreColumn);
            dataDefinition.TableDefinition.Headers.push(drivingFuelColumn);

            return this.dashboardService.populateDataDefinition(dataDefinition).then(dataDefinition => {
                return dataDefinition;
            });
        });
    }
}

export class DriversPageComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            driver: "<",
            score: "<"
        };

        this.controller = Controller;
        this.template = require("./driversPage.component.html");
    }
}
